<template>
  <div class="groupon-verify page-pad">
    <el-card shadow="never" class="verify-card">
      <div slot="header" class="hdr">
        <div class="hdr-left">
          <span class="hdr-title">团购扫码核销</span>
          <span class="hdr-sub">验券确认后立即核销</span>
        </div>
        <el-button type="text" class="link-cfg" @click="$router.push('/groupon-settings')">
          对接配置 ›
        </el-button>
      </div>

      <div class="tip-bar">
        扫码或输入券码 → 验券 → 确认核销。美团可按手机号查询；抖音/饿了么请使用券码。
      </div>

      <div class="platform-row">
        <button
          v-for="p in platforms"
          :key="p.value"
          type="button"
          class="plat-btn"
          :class="{ on: platform === p.value }"
          @click="platform = p.value"
        >
          {{ p.label }}
        </button>
      </div>

      <div class="field-block">
        <div class="field-label">券码</div>
        <div class="field-row">
          <el-input
            v-model="code"
            clearable
            class="field-input"
            placeholder="扫码枪扫入或粘贴券码"
            @keyup.enter.native="prepare"
          />
          <el-button
            type="primary"
            class="field-btn"
            :loading="preparing"
            @click="prepare"
          >
            验券
          </el-button>
        </div>
      </div>

      <div class="field-block">
        <div class="field-label">摄像头</div>
        <el-button class="cam-btn" @click="toggleScan">
          <i :class="scanning ? 'el-icon-video-camera-solid' : 'el-icon-camera'"></i>
          {{ scanning ? '停止扫码' : '打开摄像头扫码' }}
        </el-button>
        <div class="scan-area" v-show="scanning">
          <video ref="scanVideo" class="scan-video" playsinline></video>
          <div class="scan-hint">对准二维码，识别后自动验券</div>
        </div>
        <div v-if="scanError" class="scan-error">{{ scanError }}</div>
      </div>

      <div class="divider-line">
        <span>按手机号查券（美团）</span>
      </div>

      <div class="field-block">
        <div class="field-label">手机号</div>
        <div class="field-row">
          <el-input
            v-model="phone"
            maxlength="11"
            clearable
            class="field-input"
            placeholder="11 位手机号"
            @keyup.enter.native="queryMobile"
          />
          <el-button class="field-btn field-btn-outline" :loading="querying" @click="queryMobile">
            查询
          </el-button>
        </div>
      </div>

      <div v-if="prepareResult" class="result-card">
        <div class="result-title">验券结果</div>
        <div class="result-row">
          <span class="k">平台</span>
          <span>{{ platformLabel(prepareResult.platform) }}</span>
        </div>
        <div class="result-row">
          <span class="k">名称</span>
          <span>{{ prepareResult.product_name || prepareResult.title || '—' }}</span>
        </div>
        <div class="result-row">
          <span class="k">面额</span>
          <span class="val">
            ¥{{ Number(prepareResult.value || prepareResult.face_value || 0).toFixed(2) }}
          </span>
        </div>
        <div class="result-row" v-if="prepareResult.receipt_code_mask">
          <span class="k">券码</span>
          <span>{{ prepareResult.receipt_code_mask }}</span>
        </div>
        <div class="result-actions">
          <el-button class="btn-ghost" @click="prepareResult = null">取消</el-button>
          <el-button type="primary" class="btn-solid" :loading="consuming" @click="consume">
            确认核销
          </el-button>
        </div>
      </div>

      <div v-if="mobileItems.length" class="mobile-list">
        <div class="result-title">手机号查到的券</div>
        <div
          v-for="item in mobileItems"
          :key="item.id || item.groupon_wallet_id"
          class="mobile-item"
        >
          <div class="mobile-main">
            <span class="plat">{{ item.platform_label || item.platform }}</span>
            <span class="name">{{ item.product_name }}</span>
            <span class="val">¥{{ Number(item.value || 0).toFixed(2) }}</span>
          </div>
          <el-button type="primary" size="mini" :loading="consuming" @click="consumeItem(item)">
            核销
          </el-button>
        </div>
      </div>

      <div v-if="lastMessage" class="last-msg">{{ lastMessage }}</div>
    </el-card>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'
import { canUseCamera, startQrCameraScan, cameraSupportHint } from '../utils/qrCameraScan'
import {
  alertGrouponMaintenanceIfNeeded,
  alertGrouponMaintenance,
  isPrivilegedAdminUser
} from '../utils/grouponGate'

const PLATFORM_LABELS = { meituan: '美团', douyin: '抖音', eleme: '饿了么' }

export default {
  name: 'GrouponVerify',
  data() {
    return {
      platforms: [
        { value: 'meituan', label: '美团' },
        { value: 'douyin', label: '抖音' },
        { value: 'eleme', label: '饿了么' }
      ],
      platform: 'meituan',
      code: '',
      phone: '',
      preparing: false,
      consuming: false,
      querying: false,
      prepareResult: null,
      mobileItems: [],
      lastMessage: '',
      scanning: false,
      scanError: '',
      scanStream: null,
      _qrScanStop: null,
      grouponEnabled: true
    }
  },
  async mounted() {
    await this.loadGate()
  },
  beforeDestroy() {
    this.stopScan()
  },
  methods: {
    async loadGate() {
      try {
        const resp = await this.$api.get('/groupon/status')
        if (resp && resp.code === 200 && resp.data) {
          this.grouponEnabled = resp.data.groupon_enabled !== false
          if (!this.grouponEnabled && !isPrivilegedAdminUser()) {
            await alertGrouponMaintenance()
          }
        }
      } catch (_e) {
        /* ignore */
      }
    },
    ensureGrouponAllowed() {
      if (this.grouponEnabled || isPrivilegedAdminUser()) return true
      alertGrouponMaintenance()
      return false
    },
    platformLabel(p) {
      return PLATFORM_LABELS[p] || p || '—'
    },
    async prepare() {
      if (!this.ensureGrouponAllowed()) return
      const code = String(this.code || '').trim()
      if (!code) {
        this.$message.warning('请输入或扫描券码')
        return
      }
      this.preparing = true
      this.lastMessage = ''
      try {
        const resp = await this.$api.post('/groupon/cashier/prepare', {
          platform: this.platform,
          code,
          phone: String(this.phone || '').replace(/\D/g, '')
        })
        if (resp && resp.code === 200 && resp.data) {
          const wallet = resp.data.wallet || {}
          this.prepareResult = {
            ...wallet,
            groupon_wallet_id: wallet.groupon_wallet_id || wallet.id
          }
          this.lastMessage = resp.message || '验券成功'
          this.$message.success(this.lastMessage)
        } else {
          this.$message.error((resp && resp.message) || '验券失败')
        }
      } catch (e) {
        if (alertGrouponMaintenanceIfNeeded(e)) return
        this.$message.error(formatApiError(e) || '验券失败')
      } finally {
        this.preparing = false
      }
    },
    async consume() {
      const id =
        (this.prepareResult &&
          (this.prepareResult.groupon_wallet_id || this.prepareResult.id)) ||
        ''
      if (!id) return
      await this.consumeItem({ groupon_wallet_id: id, id })
      if (this.lastMessage && !this.consuming) {
        this.prepareResult = null
        this.code = ''
      }
    },
    async consumeItem(item) {
      if (!this.ensureGrouponAllowed()) return
      const wid = (item && (item.groupon_wallet_id || item.id)) || ''
      if (!wid) return
      this.consuming = true
      this.lastMessage = ''
      try {
        const resp = await this.$api.post('/groupon/cashier/consume', {
          groupon_wallet_id: wid,
          idempotency_key: `cashier-ui:${wid}:${Date.now()}`
        })
        if (resp && resp.code === 200) {
          this.lastMessage = resp.message || '核销成功'
          this.$message.success(this.lastMessage)
          this.mobileItems = this.mobileItems.filter(
            (x) => String(x.id || x.groupon_wallet_id) !== String(wid)
          )
          if (
            this.prepareResult &&
            String(this.prepareResult.groupon_wallet_id || this.prepareResult.id) === String(wid)
          ) {
            this.prepareResult = null
            this.code = ''
          }
        } else {
          this.$message.error((resp && resp.message) || '核销失败')
        }
      } catch (e) {
        if (alertGrouponMaintenanceIfNeeded(e)) return
        this.$message.error(formatApiError(e) || '核销失败')
      } finally {
        this.consuming = false
      }
    },
    async queryMobile() {
      if (!this.ensureGrouponAllowed()) return
      const phone = String(this.phone || '').replace(/\D/g, '')
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('请输入 11 位手机号')
        return
      }
      this.querying = true
      this.mobileItems = []
      try {
        const resp = await this.$api.post('/groupon/cashier/query-mobile', { phone })
        if (resp && resp.code === 200) {
          this.mobileItems = (resp.data && resp.data.items) || []
          this.$message.success(
            this.mobileItems.length
              ? `查到 ${this.mobileItems.length} 张`
              : '暂无可用平台券'
          )
        } else {
          this.$message.error((resp && resp.message) || '查询失败')
        }
      } catch (e) {
        if (alertGrouponMaintenanceIfNeeded(e)) return
        this.$message.error(formatApiError(e) || '查询失败')
      } finally {
        this.querying = false
      }
    },
    async toggleScan() {
      if (!this.ensureGrouponAllowed()) return
      if (this.scanning) {
        this.stopScan()
        return
      }
      this.scanError = ''
      if (!canUseCamera()) {
        this.$message.warning(cameraSupportHint() || '当前浏览器不支持摄像头扫码')
        return
      }
      const videoEl = this.$refs.scanVideo
      if (!videoEl) return
      try {
        this.scanning = true
        const session = await startQrCameraScan(videoEl, {
          isActive: () => !!this.scanning,
          onResult: async (raw) => {
            this.code = String(raw || '').trim()
            this.stopScan()
            await this.prepare()
          }
        })
        this.scanStream = session.stream
        this._qrScanStop = session.stop
      } catch (e) {
        const name = e && e.name ? String(e.name) : ''
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          this.scanError = '未获得摄像头权限'
        } else {
          this.scanError = '无法打开摄像头，请检查权限或使用 HTTPS'
        }
        this.stopScan()
      }
    },
    stopScan() {
      this.scanning = false
      if (typeof this._qrScanStop === 'function') {
        try {
          this._qrScanStop()
        } catch (_e) {}
        this._qrScanStop = null
      }
      if (this.scanStream) {
        try {
          this.scanStream.getTracks().forEach((t) => t.stop())
        } catch (_e) {}
        this.scanStream = null
      }
      const videoEl = this.$refs.scanVideo
      if (videoEl) videoEl.srcObject = null
    }
  }
}
</script>

<style scoped>
.page-pad {
  padding: 16px;
}
.verify-card {
  border-radius: 12px;
  max-width: 640px;
}
.hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hdr-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.hdr-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.hdr-sub {
  font-size: 12px;
  color: #94a3b8;
}
.link-cfg {
  color: #2bb673 !important;
  font-weight: 600;
}
.tip-bar {
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-size: 13px;
  line-height: 1.5;
}
.platform-row {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.plat-btn {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.plat-btn.on {
  border-color: #2bb673;
  background: #ecfdf5;
  color: #047857;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.12);
}
.field-block {
  margin-bottom: 18px;
}
.field-label {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 8px;
}
.field-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}
.field-input {
  flex: 1;
}
.field-input >>> .el-input__inner {
  height: 44px;
  line-height: 44px;
  border-radius: 10px;
  border: 1.5px solid #dbe3ea;
  font-size: 14px;
  padding: 0 14px;
}
.field-input >>> .el-input__inner:focus {
  border-color: #2bb673;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.12);
}
.field-btn {
  min-width: 96px;
  height: 44px;
  border-radius: 10px !important;
  font-weight: 700;
  background: #2bb673 !important;
  border-color: #2bb673 !important;
}
.field-btn-outline {
  background: #fff !important;
  border: 1.5px solid #2bb673 !important;
  color: #2bb673 !important;
}
.cam-btn {
  height: 40px;
  border-radius: 10px;
  border: 1.5px dashed #86efac;
  background: #f0fdf4;
  color: #047857;
  font-weight: 600;
}
.scan-area {
  margin-top: 12px;
  border-radius: 12px;
  overflow: hidden;
  background: #0f172a;
}
.scan-video {
  width: 100%;
  max-height: 280px;
  display: block;
  object-fit: cover;
}
.scan-hint {
  padding: 8px 12px;
  font-size: 12px;
  color: #94a3b8;
  background: #1e293b;
}
.scan-error {
  margin-top: 8px;
  color: #dc2626;
  font-size: 13px;
}
.divider-line {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0 18px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
}
.divider-line::before,
.divider-line::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}
.result-card,
.mobile-list {
  margin-top: 8px;
  padding: 16px;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  background: #f0fdf4;
}
.result-title {
  font-weight: 700;
  margin-bottom: 12px;
  color: #065f46;
}
.result-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 14px;
}
.result-row .k {
  width: 48px;
  color: #64748b;
  flex-shrink: 0;
}
.result-row .val {
  font-weight: 700;
  color: #b45309;
}
.result-actions {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.btn-ghost {
  border-radius: 10px !important;
}
.btn-solid {
  border-radius: 10px !important;
  background: #2bb673 !important;
  border-color: #2bb673 !important;
  font-weight: 700;
}
.mobile-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px dashed #a7f3d0;
}
.mobile-item:last-child {
  border-bottom: none;
}
.mobile-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.plat {
  font-size: 12px;
  font-weight: 700;
  color: #0f766e;
  background: #ccfbf1;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
}
.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.val {
  font-weight: 700;
  color: #b45309;
  flex-shrink: 0;
}
.last-msg {
  margin-top: 12px;
  font-size: 13px;
  color: #047857;
}
</style>
